import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { Exclude, Expose } from "class-transformer";
import { IsNumber, IsOptional, IsString } from "class-validator";
import { Subject } from "rxjs";
import { v4 as uuidv4 } from "uuid";

export class MeasurementLine extends FloorplanItem {
  @IsString()
  @Expose({ name: "text" })
  private _text: string;

  @IsNumber()
  @Expose({ name: "width" })
  private _width: number;

  @IsNumber()
  @Expose({ name: "height" })
  private _height: number;

  @IsNumber()
  @Expose({ name: "rotation" })
  private _rotation: number;

  @IsNumber()
  @IsOptional()
  @Expose({ name: "referenceLength" })
  public _referenceLength?: number;

  @Exclude()
  public lengthChanged: Subject<{ length: number }>;

  @Exclude()
  referenceLengthChanged: Subject<{ referenceLength: number | undefined }>;

  constructor(
    floorplan: Floorplan,
    id: string,
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number,
    text: string,
  ) {
    super(floorplan, id, x, y);
    this._text = text;
    this._width = width;
    this._height = height;
    this._rotation = rotation;
    this.lengthChanged = new Subject();
    this.referenceLengthChanged = new Subject();
    this.updateReferenceLength(this._width);
  }

  get width(): number {
    return this._width;
  }

  get height(): number {
    return this._height;
  }

  get rotation(): number {
    return this._rotation;
  }

  get referenceLength(): number | undefined {
    return this._referenceLength;
  }

  isReferenceMeasureSet() {
    if (this._floorplan) return !!this._floorplan.referenceMeasure;
    return false;
  }

  get text(): string {
    return this._text;
  }

  set text(text: string) {
    if (this._text === text) return;
    this._text = text;
    this._floorplan.publishUpdate(FloorplanEventType.MEASUREMENT_LINE_UPDATED, this);
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const height = this.height;
    const rotation = this.rotation;
    const text = this.text;
    const width = this.width;

    return (x: number, y: number) => MeasurementLine.create(this._floorplan, x, y, width, height, rotation, text);
  }

  override delete() {
    this._floorplan.deleteMeasurementLine(this);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.measurementLines;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as MeasurementLine,
        this._floorplan.measurementLines,
        FloorplanEventType.MEASUREMENT_LINE_ADDED,
        () => this._floorplan.project.setMeasurementLinePositionIds(),
        afterDuplicateAdded as ((duplicate: MeasurementLine) => void) | undefined,
      );
  }

  updateLength(referenceLength: number) {
    this._width = referenceLength * this._floorplan.getReferenceScale();
    this.updateForm(this._width, this._height, this._rotation, this.x, this.y);
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_SIZE_CHANGED_BY_INPUT, this);
  }

  updateReferenceLength(length: number) {
    if (this.isReferenceMeasureSet()) {
      this._referenceLength = Math.round((length / this._floorplan.getReferenceScale()) * 100) / 100;
    } else {
      this._referenceLength = undefined;
    }
  }

  updateForm(width: number, height: number, rotation: number, x: number, y: number) {
    this.lengthChanged.next({ length: width });
    this.updateReferenceLength(width);
    this._width = width;
    this._height = height;
    this._rotation = rotation;
    this.updatePosition(x, y);
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.measurementLinesItemTextVisible;
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshMeasurementLinesLock();
  }

  static create(floorplan: Floorplan, x: number, y: number, width: number, height: number, rotation: number, text: string) {
    return new MeasurementLine(floorplan, uuidv4(), x, y, width, height, rotation, text);
  }
}
